import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';

/**
 * A one-line message inside a page: a failure (red, the THEME register's
 * "alerts") or a confirmation (brand teal). Hairline border and a soft plate,
 * no glow. Label 0.9375rem with a 1.125rem icon, per the proportion rule.
 *
 * `live="alert"` makes a screen reader speak the message the moment it appears;
 * use it for failures caused by an action. A confirmation should sit inside a
 * live region that is always mounted (`role="status"`), because a region that
 * appears together with its text is not reliably announced.
 */
export interface InlineAlertProps {
  tone?: 'error' | 'success';
  children: ReactNode;
  /** An action at the inline end, such as a retry button. */
  action?: ReactNode;
  live?: 'alert';
  className?: string;
}

const TONES: Record<NonNullable<InlineAlertProps['tone']>, string> = {
  error:
    'border-[#DF2F2B]/25 bg-[var(--pv-color-red-soft)] text-[#C62828] dark:border-[#FF8A80]/30 dark:text-[var(--pv-color-red)]',
  success:
    'border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)]',
};

export default function InlineAlert({ tone = 'error', children, action, live, className = '' }: InlineAlertProps) {
  const Icon = tone === 'error' ? AlertTriangle : CheckCircle2;
  return (
    <div
      role={live}
      className={`flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border px-4 py-3 text-[0.9375rem] leading-relaxed ${TONES[tone]} ${className}`.trim()}
    >
      <Icon className="h-[1.125rem] w-[1.125rem] shrink-0" aria-hidden="true" />
      <p className="min-w-0 flex-1">{children}</p>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
